import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Badge } from "../ui/badge";
import { usd } from "../../lib/affApi";

const STATUS_CLASS = {
  Trial: "bg-sky-50 text-sky-700 border-sky-200",
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Commission ended": "bg-zinc-100 text-zinc-600 border-zinc-200",
  Churned: "bg-amber-50 text-amber-700 border-amber-200",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export function CustomersTable({ customers }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="aff-customers">
      <div className="px-4 py-3 border-b border-zinc-100 flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium">Referred customers</div>
        <div className="text-xs text-zinc-500" data-testid="aff-anonymity-note">Customer identities are protected; you see spend and status only.</div>
      </div>
      {customers.length === 0 ? (
        <div className="p-8 text-center text-sm text-zinc-500" data-testid="aff-customers-empty">No sign-ups yet — share your link to get started.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs text-zinc-500">
              <tr>
                {["Customer", "Type", "Yacht size", "Signed up", "Status", "Months left", "Spend to date", "Commission"].map((h) => (
                  <th key={h} className="text-left font-medium px-4 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {customers.map((c) => (
                <tr key={c.id} data-testid={`aff-customer-row-${c.id}`}>
                  <td className="px-4 py-2.5 font-medium whitespace-nowrap">{c.label}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{c.kind}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{c.size_band}{c.yachts > 1 ? ` · ${c.yachts} yachts` : ""}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(c.signed_up)}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline" className={STATUS_CLASS[c.status] || ""}>{c.status}</Badge></td>
                  <td className="px-4 py-2.5">{c.months_remaining == null ? "—" : `${c.months_remaining} / 12`}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{usd(c.spend_to_date)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap font-medium text-emerald-700">{usd(c.commission_to_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function EarningsChart({ data }) {
  const rows = data.map((d) => ({ ...d, label: new Date(`${d.month}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "short" }) }));
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="aff-chart">
      <div className="font-medium mb-3">Last 12 months <span className="text-xs text-zinc-500 font-normal">— current month is a live estimate</span></div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48} />
            <Tooltip formatter={(v) => usd(v)} labelFormatter={(_, p) => p?.[0]?.payload?.month} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="spend" name="Customer spend" fill="#d4d4d8" radius={[3, 3, 0, 0]} />
            <Bar dataKey="commission" name="Your commission" fill="#059669" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function PayoutsTab({ payouts, cards }) {
  return (
    <div className="space-y-4" data-testid="aff-payouts">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-zinc-200 bg-white p-4"><div className="text-xs text-zinc-500">Open balance</div><div className="font-display text-2xl mt-1" data-testid="aff-open-balance">{usd(cards.open_balance)}</div></div>
        <div className="rounded-xl border border-zinc-200 bg-white p-4"><div className="text-xs text-zinc-500">Minimum payout</div><div className="font-display text-2xl mt-1" data-testid="aff-min-payout">{usd(cards.min_payout)}</div><div className="text-xs text-zinc-400 mt-0.5">paid on the 1st of each month</div></div>
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        {payouts.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500" data-testid="aff-payouts-empty">No payouts yet. Once your balance passes the minimum, a payout appears here on the 1st.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs text-zinc-500"><tr>{["Date", "Period", "Amount", "Status", "Reference"].map((h) => <th key={h} className="text-left font-medium px-4 py-2">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-zinc-100">
              {payouts.map((p) => (
                <tr key={p.id} data-testid={`aff-payout-row-${p.id}`}>
                  <td className="px-4 py-2.5">{fmtDate(p.paid_at || p.created_at)}</td>
                  <td className="px-4 py-2.5">{p.period}</td>
                  <td className="px-4 py-2.5 font-medium">{usd(p.amount)}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline" className={p.status === "paid" ? STATUS_CLASS.Active : "bg-amber-50 text-amber-700 border-amber-200"}>{p.status === "paid" ? "Paid" : "Pending"}</Badge></td>
                  <td className="px-4 py-2.5 text-zinc-600">{p.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
